import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, AlertDescription } from '../ui/alert';
import ChartRenderer from './ChartRenderer';

const ROWS_PER_PAGE = 100;

/**
 * OutputRenderer
 * Renders an AnalyticsResponse object from the backend.
 * Phase 1: text and table output only.
 * Chart output placeholder shown for bar_chart, pie_chart, line_chart.
 */
const OutputRenderer = ({ response, onConfirm }) => {
  if (!response) return null;

  // ── Partial match confirmation (action === 'confirm') ──────────────────────
  // Backend found a close-but-not-exact match and wants user to confirm.
  if (response.action === 'confirm') {
    return (
      <div className="rounded-lg border p-4 text-sm bg-amber-50 border-amber-300 text-amber-900">
        <p className="font-semibold mb-1">Did you mean this question?</p>
        <p className="italic mb-3">&ldquo;{response.suggested_question}&rdquo;</p>
        {onConfirm && (
          <div className="flex gap-2 mt-2">
            <button
              onClick={() => onConfirm(true, response)}
              className="px-3 py-1.5 text-xs font-medium rounded-md bg-amber-600 text-white hover:bg-amber-700 transition-colors"
            >
              Yes, use this
            </button>
            <button
              onClick={() => onConfirm(false, response)}
              className="px-3 py-1.5 text-xs font-medium rounded-md border border-amber-400 text-amber-800 hover:bg-amber-100 transition-colors"
            >
              No, answer as typed
            </button>
          </div>
        )}
      </div>
    );
  }

  // ── Dataset disambiguation — user must pick which dataset they meant ───────
  if (response.action === 'dataset_disambiguation') {
    return (
      <div className="rounded-lg border p-4 text-sm bg-amber-50 border-amber-300 text-amber-900">
        <p className="font-semibold mb-1">📂 Multiple matching datasets</p>
        <p className="mb-3 leading-relaxed">{response.question || 'This question could apply to multiple datasets. Which did you mean?'}</p>
        <div className="flex flex-col gap-2">
          {(response.options || []).map((opt, i) => (
            <button
              key={i}
              onClick={() => onConfirm && onConfirm('select_group', { ...response, selected_group_id: opt.group_id })}
              className="text-left px-4 py-3 rounded-lg border border-amber-300 bg-white hover:bg-amber-100 transition-colors"
            >
              <span className="font-medium text-gray-800">{opt.label}</span>
              {opt.filenames && opt.filenames.length > 0 && (
                <span className="ml-2 text-xs text-gray-500">({opt.filenames.join(', ')})</span>
              )}
              {opt.row_count > 0 && (
                <span className="ml-2 text-xs text-gray-400">{opt.row_count.toLocaleString()} rows</span>
              )}
            </button>
          ))}
        </div>
      </div>
    );
  }

  // ── Resolver error — no columns found or no join path ─────────────────────
  if (response.action === 'resolver_error') {
    return (
      <div className="rounded-lg border p-4 text-sm bg-red-50 border-red-300 text-red-900">
        <p className="font-semibold mb-1">
          {response.reason === 'no_columns_found' ? '🔍 Could not identify dataset' : '🔗 No common key between datasets'}
        </p>
        <p className="leading-relaxed">{response.message}</p>
      </div>
    );
  }

  // ── PDF still indexing — transient, friendly blue notice ─────────────────
  if (response.action === 'pdf_indexing') {
    return (
      <div className="rounded-lg border p-4 text-sm bg-blue-50 border-blue-300 text-blue-900">
        <p className="font-semibold mb-1">⏳ PDF is still being indexed</p>
        <p className="leading-relaxed">{response.message}</p>
      </div>
    );
  }

  // Handle special action responses from the backend
  // (incompatible_datasets, clarify, validation_failed, etc.)
  if (response.action || response.error) {
    // Extend the known warning actions
    const isWarning = ['incompatible_datasets', 'clarify', 'dataset_disambiguation', 'resolver_error'].includes(response.action);
    const msg = response.message || response.error || 'An error occurred.';
    const details = Array.isArray(response.details) ? response.details : [];
    return (
      <div className={`rounded-lg border p-4 text-sm ${
        isWarning
          ? 'bg-amber-50 border-amber-300 text-amber-900'
          : 'bg-red-50 border-red-300 text-red-900'
      }`}>
        <p className="font-semibold mb-1">
          {response.action === 'incompatible_datasets' && 'Cannot combine these datasets'}
          {response.action === 'clarify' && 'Clarification needed'}
          {response.action === 'validation_failed' && 'Query could not be validated'}
          {response.action === 'execution_failed' && 'Query execution failed'}
          {response.action === 'pdf_error' && 'PDF query failed'}
          {response.error && !response.action && 'Error'}
        </p>
        <p className="leading-relaxed">{msg}</p>
        {details.length > 0 && (
          <ul className="mt-2 list-disc list-inside space-y-1 text-xs opacity-80">
            {details.map((d, i) => <li key={i}>{d}</li>)}
          </ul>
        )}
        {response.action === 'clarify' && response.clarification_question && (
          <p className="mt-2 font-medium">{response.clarification_question}</p>
        )}
      </div>
    );
  }

  // ── parallel_results — resolver-triggered independent group charts ──────────
  if (response.type === 'parallel_results') {
    const parallelGroupCharts = response.group_charts || [];
    const parallelResults     = response.results     || [];
    return (
      <div className="flex flex-col gap-4">
        {/* Stale warning — shown when graph changed since last run */}
        {response.stale_warning && (
          <div className="rounded-lg border border-yellow-400 bg-yellow-50 px-4 py-3 text-sm text-yellow-900 flex items-start gap-2">
            <span className="text-base">⚠️</span>
            <p className="leading-relaxed">{response.stale_warning}</p>
          </div>
        )}
        <div className="rounded-lg border border-teal-200 bg-teal-50 px-4 py-2 text-xs text-teal-800 flex items-center gap-2">
          <span className="font-semibold">📊 Parallel analysis</span>
          <span className="text-teal-400">—</span>
          <span>Each dataset was analysed independently</span>
        </div>
        {parallelGroupCharts.length > 0
          ? parallelGroupCharts.map((gc, i) => (
              <div key={i} className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                  <span className="text-sm font-semibold text-gray-700">{gc.label}</span>
                </div>
                <div className="p-4">
                  {gc.output_type === 'text' && <TextOutput data={gc.data} />}
                  {gc.output_type === 'table' && <TableOutput data={gc.data} />}
                  {['bar_chart', 'pie_chart', 'line_chart'].includes(gc.output_type) && (
                    <div style={{ minHeight: 340 }}>
                      <ChartRenderer outputType={gc.output_type} data={gc.data} title={gc.label} />
                    </div>
                  )}
                  {gc.interpretation && (
                    <div className="mt-3 bg-blue-50 border border-blue-100 rounded p-3">
                      <p className="text-xs text-gray-700 leading-relaxed">{gc.interpretation}</p>
                    </div>
                  )}
                </div>
              </div>
            ))
          : parallelResults.map((r, i) => (
              <div key={i} className="border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-600">
                <span className="font-semibold text-gray-700">{r.source_label}</span>
                {r.source_files && r.source_files.length > 0 && (
                  <span className="ml-2 text-xs text-gray-400">({r.source_files.join(', ')})</span>
                )}
              </div>
            ))
        }

      </div>
    );
  }

  const {
    output_type,
    data,
    interpretation,
    suppressed_groups,
    source_datasets,
    source_chunks,
    route,
    strategy,
    join_key,
    group_charts,
    join_decision,
    stale_warning,
  } = response;

  return (
    <div className="flex flex-col gap-4">
      {/* Stale query warning — graph changed since this query last ran */}
      {stale_warning && (
        <div className="rounded-lg border border-yellow-400 bg-yellow-50 px-4 py-3 text-sm text-yellow-900 flex items-start gap-2">
          <span className="text-base">⚠️</span>
          <p className="leading-relaxed">{stale_warning}</p>
        </div>
      )}

      {/* Suppression notice — shown whenever any group was hidden for privacy */}
      {suppressed_groups && (
        <Alert className="border-amber-300 bg-amber-50">
          <AlertDescription className="text-amber-800 text-sm">
            <strong>Privacy Notice:</strong> One or more groups have been suppressed because
            they contained fewer than 5 patients (HIPAA minimum cell size requirement).
          </AlertDescription>
        </Alert>
      )}

      {/* Analysis method badge — tells the user HOW multi-dataset results were produced */}
      {strategy === 'join' && join_key && (
        <div className="rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-2 text-xs text-indigo-800 flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <span className="font-semibold">🔗 Joined analysis</span>
            <span className="text-indigo-400">—</span>
            <span>
              {join_decision?.join_method === 'suffix'
                ? <>Linked on <strong>{join_key}</strong> using numeric suffix ({join_decision.overlap_pct}% overlap)</>
                : <>Linked on common key <strong>{join_key}</strong> — exact values matched</>
              }
            </span>
          </div>
          {join_decision?.decision_reason && (
            <p className="text-indigo-600 text-[11px] leading-relaxed">{join_decision.decision_reason}</p>
          )}
        </div>
      )}
      {strategy === 'per_dataset' && source_datasets && source_datasets.length > 1 && (
        <div className="rounded-lg border border-teal-200 bg-teal-50 px-4 py-2 text-xs text-teal-800 flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <span className="font-semibold">📊 Independent analysis</span>
            <span className="text-teal-400">—</span>
            <span>Each dataset was analysed separately</span>
          </div>
          {join_decision?.decision_reason && (
            <p className="text-teal-600 text-[11px] leading-relaxed">{join_decision.decision_reason}</p>
          )}
        </div>
      )}

      {/* Main output */}
      {output_type === 'text' && <TextOutput data={data} />}
      {output_type === 'table' && <TableOutput data={data} />}
      {['bar_chart', 'pie_chart', 'line_chart'].includes(output_type) && (
        <div style={{ minHeight: 340 }}>
          <ChartRenderer
            outputType={output_type}
            data={data}
            title={response.question_matched || ''}
          />
        </div>
      )}
      {/* Per-dataset charts — one labelled chart block per dataset */}
      {output_type === 'per_dataset_charts' && Array.isArray(group_charts) && group_charts.map((gc, i) => (
        <div key={i} className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
            <span className="text-sm font-semibold text-gray-700">{gc.label}</span>
          </div>
          <div className="p-4" style={{ minHeight: 360 }}>
            <ChartRenderer outputType={gc.output_type} data={gc.data} title={gc.label} />
          </div>
        </div>
      ))}

      {/* PDF source citations — only for RAG responses */}
      {route === 'rag' && source_chunks && source_chunks.length > 0 && (
        <SourceCitations chunks={source_chunks} />
      )}

      {/* Source datasets footer — always shown so the user knows which file(s) contributed */}
      {source_datasets && source_datasets.length > 0 && (
        <p className="text-xs text-gray-400">
          {source_datasets.length === 1 ? 'Source' : 'Sources'}: {source_datasets.join(', ')}
        </p>
      )}
    </div>
  );
};

/* ── Text Output ──────────────────────────────────────────────────────────── */

const TextOutput = ({ data }) => {
  const text = data?.text || JSON.stringify(data, null, 2);

  // Parse lines like "patient_count: 37" or "avg_age: 54.2" into key/value pairs
  const lines = text.trim().split('\n').map(l => l.trim()).filter(Boolean);
  const kvPairs = lines.map(line => {
    const idx = line.indexOf(':');
    if (idx === -1) return null;
    const key   = line.slice(0, idx).trim();
    const value = line.slice(idx + 1).trim();
    return { key, value };
  }).filter(Boolean);

  const allKv = kvPairs.length === lines.length;

  // Single scalar result (e.g. patient_count: 37) → large readable display
  if (allKv && kvPairs.length === 1) {
    const { key, value } = kvPairs[0];
    const label = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    const isNumeric = !isNaN(Number(value)) && value !== '';
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg px-6 py-5 flex items-center gap-4">
        {isNumeric
          ? <span className="text-4xl font-bold text-gray-900">{Number(value).toLocaleString()}</span>
          : <span className="text-2xl font-semibold text-gray-900">{value}</span>
        }
        <span className="text-sm text-gray-500">{label}</span>
      </div>
    );
  }

  // Multiple key:value pairs → clean label/value list
  if (allKv && kvPairs.length > 1) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-5 flex flex-col gap-2">
        {kvPairs.map(({ key, value }, i) => {
          const label = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
          return (
            <div key={i} className="flex items-baseline gap-2">
              <span className="text-sm text-gray-500 min-w-[120px]">{label}:</span>
              <span className="text-sm font-semibold text-gray-900">{value}</span>
            </div>
          );
        })}
      </div>
    );
  }

  // Fallback: plain text
  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-5">
      <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">{text}</p>
    </div>
  );
};

/* ── Table Output ─────────────────────────────────────────────────────────── */

const TableOutput = ({ data }) => {
  const [sortCol, setSortCol]   = useState(null);
  const [sortDir, setSortDir]   = useState('asc');
  const [page, setPage]         = useState(0);

  const headers = data?.headers || [];
  const rawRows = data?.rows    || [];

  const handleSort = (colIndex) => {
    if (sortCol === colIndex) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortCol(colIndex);
      setSortDir('asc');
    }
    setPage(0);
  };

  const sortedRows = React.useMemo(() => {
    if (sortCol === null) return rawRows;
    return [...rawRows].sort((a, b) => {
      const av = a[sortCol];
      const bv = b[sortCol];
      // Try numeric sort first
      const an = parseFloat(av);
      const bn = parseFloat(bv);
      if (!isNaN(an) && !isNaN(bn)) {
        return sortDir === 'asc' ? an - bn : bn - an;
      }
      return sortDir === 'asc'
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av));
    });
  }, [rawRows, sortCol, sortDir]);

  const totalPages = Math.ceil(sortedRows.length / ROWS_PER_PAGE);
  const pageRows   = sortedRows.slice(
    page * ROWS_PER_PAGE,
    (page + 1) * ROWS_PER_PAGE
  );

  if (headers.length === 0) {
    return <p className="text-sm text-gray-400 italic">No data to display.</p>;
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50">
          <tr>
            {headers.map((h, i) => (
              <th
                key={i}
                onClick={() => handleSort(i)}
                className="px-4 py-3 text-left font-semibold text-gray-700 cursor-pointer
                           select-none hover:bg-gray-100 whitespace-nowrap"
              >
                {h}
                {sortCol === i ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 bg-white">
          {pageRows.map((row, ri) => (
            <tr key={ri} className="hover:bg-gray-50">
              {row.map((cell, ci) => {
                const isSuppressed =
                  typeof cell === 'string' && cell.startsWith('Suppressed');
                return (
                  <td
                    key={ci}
                    className={`px-4 py-2 ${
                      isSuppressed ? 'italic text-gray-400' : 'text-gray-800'
                    }`}
                  >
                    {cell}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-t text-sm text-gray-600">
          <span>
            Page {page + 1} of {totalPages} ({sortedRows.length} rows)
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="px-3 py-1 rounded border disabled:opacity-40 hover:bg-gray-100"
            >
              Previous
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page === totalPages - 1}
              className="px-3 py-1 rounded border disabled:opacity-40 hover:bg-gray-100"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/* ── Source Citations ─────────────────────────────────────────────────────── */

const SourceCitations = ({ chunks }) => {
  const [open, setOpen] = useState(false);

  // Collect unique page numbers across all chunks, sorted numerically
  const allPages = [...new Set(
    chunks.flatMap(c => c.source_pages || [])
  )].sort((a, b) => a - b);

  const pageLabel = allPages.length > 0
    ? `p. ${allPages.join(', ')}`
    : `${chunks.length} passage${chunks.length !== 1 ? 's' : ''}`;

  return (
    <div className="border border-gray-200 rounded-lg text-sm overflow-hidden">
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50
                   hover:bg-gray-100 transition-colors text-left"
      >
        <span className="flex items-center gap-2 text-gray-600 font-medium">
          <span className="text-base">📄</span>
          Sources — {pageLabel}
        </span>
        <span className="text-gray-400 text-xs">{open ? '▲ hide' : '▼ show'}</span>
      </button>

      {open && (
        <div className="divide-y divide-gray-100">
          {chunks.map((chunk, i) => {
            const pages = chunk.source_pages || [];
            const score = chunk.relevance_score != null
              ? `${(chunk.relevance_score * 100).toFixed(0)}% relevance`
              : null;
            const snippet = chunk.text
              ? chunk.text.trim().slice(0, 240) + (chunk.text.trim().length > 240 ? '…' : '')
              : null;

            return (
              <div key={i} className="px-4 py-3 bg-white">
                <div className="flex items-center gap-2 mb-1">
                  {pages.length > 0 && (
                    <span className="text-xs font-semibold text-blue-700 bg-blue-50
                                     border border-blue-200 rounded px-1.5 py-0.5">
                      {pages.length === 1 ? `Page ${pages[0]}` : `Pages ${pages.join(', ')}`}
                    </span>
                  )}
                  {score && (
                    <span className="text-xs text-gray-400">{score}</span>
                  )}
                </div>
                {snippet && (
                  <p className="text-xs text-gray-600 leading-relaxed">{snippet}</p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ── PropTypes ───────────────────────────────────────────────────────────── */

OutputRenderer.propTypes = {
  response: PropTypes.shape({
    output_type:      PropTypes.string,
    data:             PropTypes.object,
    interpretation:   PropTypes.string,
    suppressed_groups: PropTypes.bool,
    source_datasets:  PropTypes.arrayOf(PropTypes.string),
    source_chunks:    PropTypes.arrayOf(PropTypes.shape({
      chunk_index:    PropTypes.number,
      text:           PropTypes.string,
      source_pages:   PropTypes.arrayOf(PropTypes.number),
      relevance_score: PropTypes.number,
    })),
    route:            PropTypes.string,
    action:           PropTypes.string,
    suggested_question: PropTypes.string,
    suggested_question_id: PropTypes.string,
    original_question: PropTypes.string,
    session_id:       PropTypes.string,
  }),
  onConfirm: PropTypes.func,
};

export default OutputRenderer;
